import React, { useState } from 'react';
import {
  ChildQuestionTemplate,
  DEFAULT_BADGE_COLOR,
  FIELD_TYPE_MAP_TRANSLATION,
  MEASUREMENT_UNIT_LABEL,
  MEASUREMENT_UNIT_PLACEHOLDER,
  Option,
  Question,
  QUESTION_TYPE_MAP_TRANSLATION,
  TEMPLATE_BADGE_COLORS,
} from '../../types';
import Select from 'react-select';
import QuestionOptionsEditor from './QuestionOptionsEditor';
import { v4 as uuidv4 } from 'uuid';
import './AutoChildQuestionConfig.css';
import { FaMinus, FaPlus, FaTrashAlt } from 'react-icons/fa';

interface AutoChildQuestionConfigProps {
  question: Question;
  updateField: <K extends keyof Question>(key: K, value: Question[K]) => void;
  dir?: 'rtl' | 'ltr';
  locked?: boolean;
  labelSuffix?: string;
}

const createDefaultChildQuestionTemplate = (): ChildQuestionTemplate => ({
  id: uuidv4(),
  option_guids: [],
  text_before: '',
  text_after: '',
  question_type: 'radio',
  options: [
    { guid: uuidv4(), text: '', exclusive: false, active: true },
    { guid: uuidv4(), text: '', exclusive: false, active: true },
  ],
  badge_color: DEFAULT_BADGE_COLOR,
});

const AutoChildQuestionConfig: React.FC<AutoChildQuestionConfigProps> = ({
  question,
  updateField,
  dir = 'rtl',
  locked = false,
  labelSuffix = '',
}) => {
  const [collapsedTemplates, setCollapsedTemplates] = useState<Set<string>>(new Set());

  const templates = question.child_question_templates || [];

  const toggleCollapse = (templateId: string) => {
    setCollapsedTemplates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleAddTemplate = () => {
    const newTemplates = [...templates, createDefaultChildQuestionTemplate()];
    updateField('child_question_templates', newTemplates);
    // Also enable auto_create when adding first template
    if (!question.auto_create_child_questions) {
      updateField('auto_create_child_questions', true);
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    const newTemplates = templates.filter((t) => t.id !== templateId);
    if (newTemplates.length === 0) {
      // Deleting last template - disable auto-create
      updateField('auto_create_child_questions', false);
      updateField('child_question_templates', undefined);
    } else {
      updateField('child_question_templates', newTemplates);
    }
  };

  const updateTemplate = (templateId: string, updates: Partial<ChildQuestionTemplate>) => {
    const newTemplates = templates.map((t) => (t.id === templateId ? { ...t, ...updates } : t));
    updateField('child_question_templates', newTemplates);
  };

  const handleOptionsChange = (
    templateId: string,
    selectedOptions: readonly { value: string; label: string }[] | null,
  ) => {
    const selectedGuids = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    updateTemplate(templateId, { option_guids: selectedGuids });
  };

  const handleTemplateFieldChange = (
    templateId: string,
    field: keyof ChildQuestionTemplate,
    value: string | boolean | undefined,
  ) => {
    updateTemplate(templateId, { [field]: value });
  };

  const handleQuestionTypeChange = (templateId: string, newType: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    const updates: Partial<ChildQuestionTemplate> = { question_type: newType };

    if (newType === 'text') {
      updates.options = undefined;
    } else if (!template.options || template.options.length === 0) {
      updates.options = [
        { guid: uuidv4(), text: '', exclusive: false, active: true },
        { guid: uuidv4(), text: '', exclusive: false, active: true },
      ];
    }

    updateTemplate(templateId, updates);
  };

  const handleTemplateOptionsChange = (templateId: string, options: Option[]) => {
    updateTemplate(templateId, { options });
  };

  const getPreviewText = (template: ChildQuestionTemplate): React.ReactNode => {
    const textBefore = template.text_before || '';
    const textAfter = template.text_after || '';
    // No automatic spacing - user controls spacing in their text
    return (
      <>
        {textBefore}
        <strong>{'{תשובה}'}</strong>
        {textAfter}
      </>
    );
  };

  return (
    <>
      {question.options && question.options.length > 0 && (
        <div className='auto-child-config-templates-container'>
          {templates.map((template, index) => {
            const isCollapsed = collapsedTemplates.has(template.id);

            return (
              <div key={template.id} className='auto-child-config-template'>
                <div className='auto-child-config-template-header'>
                  <button
                    type='button'
                    className='auto-child-config-collapse-btn'
                    onClick={() => toggleCollapse(template.id)}
                    title={isCollapsed ? 'הרחב' : 'כווץ'}
                  >
                    {isCollapsed ? <FaPlus size={12} /> : <FaMinus size={12} />}
                  </button>
                  <div className='auto-child-config-template-title'>
                    תבנית שאלת ילד {templates.length > 1 ? `#${index + 1}` : ''}
                  </div>
                  <button
                    type='button'
                    className='auto-child-config-delete-btn'
                    onClick={() => handleDeleteTemplate(template.id)}
                    title='מחק תבנית'
                    disabled={locked}
                  >
                    <FaTrashAlt size={12} />
                  </button>
                </div>

                {isCollapsed ? (
                  <div className='auto-child-config-preview auto-child-config-preview-collapsed'>
                    <span
                      className='auto-child-config-color-dot'
                      style={{
                        backgroundColor: template.badge_color || DEFAULT_BADGE_COLOR,
                      }}
                    />
                    {getPreviewText(template)}
                  </div>
                ) : (
                  <>
                    <div className='auto-child-config-field-group'>
                      <div className='auto-child-config-field-label'>
                        בחר אפשרויות שיצרו שאלות ילד
                      </div>
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        value={question.options
                          .filter((opt) => template.option_guids?.includes(opt.guid))
                          .map((opt) => ({
                            value: opt.guid,
                            label: String(opt.text),
                          }))}
                        onChange={(selected) => handleOptionsChange(template.id, selected)}
                        options={question.options.map((opt) => ({
                          value: opt.guid,
                          label: String(opt.text),
                        }))}
                        placeholder='בחר אפשרויות'
                        isDisabled={locked}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className='auto-child-config-field-group'>
                      <div className='auto-child-config-field-label'>
                        טקסט לפני התשובה{labelSuffix}
                      </div>
                      <input
                        type='text'
                        className='input-field auto-child-config-input-rtl'
                        style={{ direction: dir }}
                        value={template.text_before || ''}
                        onChange={(e) =>
                          handleTemplateFieldChange(template.id, 'text_before', e.target.value)
                        }
                        placeholder='לדוגמה: כמה זמן לפני שאובחנת עם'
                      />
                    </div>

                    <div className='auto-child-config-field-group'>
                      <div className='auto-child-config-field-label'>
                        טקסט אחרי התשובה{labelSuffix}
                      </div>
                      <input
                        type='text'
                        className='input-field auto-child-config-input-rtl'
                        style={{ direction: dir }}
                        value={template.text_after || ''}
                        onChange={(e) =>
                          handleTemplateFieldChange(template.id, 'text_after', e.target.value)
                        }
                        placeholder='לדוגמה: ?'
                      />
                    </div>

                    <div className='auto-child-config-field-group'>
                      <div className='auto-child-config-field-label'>תצוגה מקדימה</div>
                      <div className='auto-child-config-preview'>{getPreviewText(template)}</div>
                    </div>

                    <div className='auto-child-config-field-group'>
                      <div className='auto-child-config-field-label'>סוג השאלה</div>
                      <select
                        className='input-field auto-child-config-select'
                        value={template.question_type || 'radio'}
                        onChange={(e) => handleQuestionTypeChange(template.id, e.target.value)}
                        disabled={locked}
                      >
                        {Object.entries(QUESTION_TYPE_MAP_TRANSLATION).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {template.question_type === 'text' && (
                      <div className='auto-child-config-field-group'>
                        <div className='auto-child-config-field-label'>סוג שדה</div>
                        <select
                          className='input-field auto-child-config-select'
                          value={template.field_type || 'text'}
                          onChange={(e) =>
                            handleTemplateFieldChange(template.id, 'field_type', e.target.value)
                          }
                          disabled={locked}
                        >
                          {Object.entries(FIELD_TYPE_MAP_TRANSLATION).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {template.question_type === 'text' && template.field_type === 'number' && (
                      <div className='auto-child-config-field-group'>
                        <div className='auto-child-config-field-label'>
                          {MEASUREMENT_UNIT_LABEL}
                          {labelSuffix}
                        </div>
                        <input
                          type='text'
                          className='input-field'
                          dir={dir}
                          value={template.measurement_unit ?? ''}
                          onChange={(e) =>
                            handleTemplateFieldChange(
                              template.id,
                              'measurement_unit',
                              e.target.value.trim() || undefined,
                            )
                          }
                          placeholder={MEASUREMENT_UNIT_PLACEHOLDER}
                        />
                      </div>
                    )}

                    <div className='checkbox-row'>
                      <input
                        type='checkbox'
                        checked={!!template.show_description}
                        onChange={(e) =>
                          handleTemplateFieldChange(
                            template.id,
                            'show_description',
                            e.target.checked,
                          )
                        }
                        disabled={locked}
                      />
                      <label className='checkbox-label'>הצג הסבר</label>
                    </div>
                    {template.show_description && (
                      <input
                        className='input-field'
                        dir={dir}
                        value={template.description ?? ''}
                        onChange={(e) =>
                          handleTemplateFieldChange(template.id, 'description', e.target.value)
                        }
                        placeholder={`טקסט הסבר${labelSuffix}`}
                      />
                    )}

                    {/* Key prefix and color side by side */}
                    <div className='auto-child-config-row'>
                      <div className='auto-child-config-field-group auto-child-config-col'>
                        <div className='auto-child-config-field-label'>קידומת מפתח</div>
                        <input
                          type='text'
                          className='input-field'
                          value={template.key_prefix || ''}
                          onChange={(e) =>
                            handleTemplateFieldChange(template.id, 'key_prefix', e.target.value)
                          }
                          placeholder='לדוגמה: diagnosed'
                          disabled={locked}
                        />
                      </div>
                      <div className='auto-child-config-field-group auto-child-config-col-color'>
                        <div className='auto-child-config-field-label'>צבע תג</div>
                        <div className='auto-child-config-color-selector'>
                          {TEMPLATE_BADGE_COLORS.map((color) => (
                            <button
                              key={color.value}
                              type='button'
                              className={`auto-child-config-color-option ${(template.badge_color || DEFAULT_BADGE_COLOR) === color.value ? 'selected' : ''}`}
                              style={{ backgroundColor: color.value }}
                              onClick={() =>
                                handleTemplateFieldChange(template.id, 'badge_color', color.value)
                              }
                              title={color.label}
                              disabled={locked}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {(template.question_type === 'select' ||
                      template.question_type === 'checkbox' ||
                      template.question_type === 'radio') && (
                      <div className='auto-child-config-field-group'>
                        <div className='auto-child-config-field-label'>
                          אפשרויות משותפות לכל שאלות הילד
                        </div>
                        <div className='auto-child-config-hint'>
                          האפשרויות האלה יופיעו בכל שאלות הילד שנוצרות מתבנית זו
                        </div>
                        <QuestionOptionsEditor
                          question={{
                            ...question,
                            options: template.options || [],
                          }}
                          updateOption={(key, value) => {
                            if (key === 'options') {
                              handleTemplateOptionsChange(template.id, value as Option[]);
                            }
                          }}
                          dir={dir}
                          locked={locked}
                          labelSuffix={labelSuffix}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <button
            type='button'
            className='auto-child-config-add-template-btn'
            onClick={handleAddTemplate}
            disabled={locked}
          >
            <FaPlus size={12} />
            <span>הוסף תבנית שאלת ילד</span>
          </button>
        </div>
      )}
    </>
  );
};

export default AutoChildQuestionConfig;
